import { ControllerInterface } from "csi/action/Controller";
import JsonResponse from "csi/comm/JsonResponse";
import { CheckboxTreeNode } from "designSystem/components/checkbox/CheckboxTree";
import { ILocaleProvider } from "designSystem/locale";
import { RadioOption } from "designSystem/components/radio/RadioGroup";
import config from "csi/config";
import { ControllerSaveFunction } from "csi/middleware/common";
import { ReactionStore } from "csi/utility/mobxUtility";
import WorkingCache from "csi/state/WorkingCache";
import { C8Node, cnl, ICache } from "csi/typings";
import { action, computed, makeObservable, observable } from "mobx";
import { ChangeEvent, createContext, useContext } from "react";
import { actionResource, restoreFromShape } from "./RestoreFromShapeService";
import { getSlimCardAttrsAsString } from "csi/middleware/card/SlimCardStore";
export class RestoreFromShapeStore extends ReactionStore {
	private readonly actionController: ControllerInterface;

	private readonly cache: ICache = new WorkingCache();

	protected readonly locale: ILocaleProvider;

	private readonly onSave: ControllerSaveFunction;

	private readonly styleSizeChartRevURL: cnl;

	private readonly _subtypeAllLocked: boolean;

	readonly titleRes: string = "RestoreFromShape";

	readonly addDSLines: CheckboxTreeNode;

	@observable.ref filteredAddDSLines: CheckboxTreeNode;

	readonly deleteDSLines: CheckboxTreeNode;

	@observable.ref filteredDeleteDSLines: CheckboxTreeNode;

	readonly updateDSLines: CheckboxTreeNode;

	@observable.ref filteredUpdateDSLines: CheckboxTreeNode;

	@observable restoreAllDSLines: boolean;

	@observable searchText: string;

	constructor(
		ac: ControllerInterface,
		locale: ILocaleProvider,
		onSave: ControllerSaveFunction,
		addDSLines: CheckboxTreeNode,
		deleteDSLines: CheckboxTreeNode,
		updateDSLines: CheckboxTreeNode,
	) {
		super();
		makeObservable(this);
		ac.bShowWait = true;

		this.actionController = ac;
		this.cache = ac.getCache();
		this.locale = locale;
		this.styleSizeChartRevURL = ac.m_urlParent;

		this.addDSLines = addDSLines;
		this.filteredAddDSLines = addDSLines;
		this.deleteDSLines = deleteDSLines;
		this.filteredDeleteDSLines	= deleteDSLines;
		this.updateDSLines = updateDSLines;
		this.filteredUpdateDSLines = updateDSLines;

		const styleSizeChart = this.masterNode;
		this._subtypeAllLocked =
			!!(config.getSubtypeSetting(styleSizeChart.Subtype, "LockShapeDimensionList") &&
				config.getSubtypeSetting(styleSizeChart.Subtype, "LockShapeDimensionValues") &&
				config.getSubtypeSetting(styleSizeChart.Subtype, "LockShapeTolerance"));

		this.restoreAllDSLines = this._subtypeAllLocked;
		this.onSave = onSave;
	}

	/** Returns true if SizeChartSubtypeSettings has locked Dimension List, Values, and Tolerance */
	get subtypeAllLocked(): boolean {
		const { _subtypeAllLocked } = this;
		return _subtypeAllLocked;
	}

	/** Returns true if delete dimensions section should be displayed */
	get shouldDisplayDeleteSection(): boolean {
		const { filteredDeleteDSLines, subtypeAllLocked } = this;
		return !subtypeAllLocked && filteredDeleteDSLines.hasChildren;
	}

	/** Returns true if update dimensions section should be displayed */
	get shouldDisplayUpdateSection(): boolean {
		const { filteredUpdateDSLines } = this;
		return filteredUpdateDSLines.hasChildren;
	}

	/** Returns true if add dimensions section should be displayed */
	get shouldDisplayAddSection(): boolean {
		const { filteredAddDSLines, restoreAllDSLines } = this;
		return restoreAllDSLines && filteredAddDSLines.hasChildren;
	}

	get revisionNode(): C8Node {
		const { cache, styleSizeChartRevURL } = this;
		return cache.get(styleSizeChartRevURL);
	}

	get masterNode(): C8Node {
		const { cache, revisionNode } = this;
		return cache.get(revisionNode.__Parent__);
	}

	/** Get the number of DSLines selected */
	@computed get dsLinesSelected(): number {
		const { filteredAddDSLines, filteredDeleteDSLines, restoreAllDSLines, filteredUpdateDSLines } = this;
		const count = filteredDeleteDSLines.selectedNodes.length + filteredUpdateDSLines.selectedNodes.length;
		return restoreAllDSLines ? count + filteredAddDSLines.selectedNodes.length : count;
	}

	/** If option chosen is 'restore all', select all delete DSLines, otherwise deselect */
	@action onChangeRestoreSettings = (event: ChangeEvent<HTMLInputElement>, value: boolean): void => {
		const { filteredDeleteDSLines } = this;
		this.restoreAllDSLines = value;
		filteredDeleteDSLines.children?.forEach((dimension) => {
			dimension.state = this.restoreAllDSLines;
		});
	};

	/** If the subtype is not all locked, add an option to only update existing Style SC DSLines */
	get restoreSettings(): RadioOption<boolean>[] {
		const { locale, subtypeAllLocked } = this;
		const options = [{
			name: locale.resource(actionResource("RestoreAllDSLines")),
			helperTextRes: actionResource("RestoreAllDSLinesHelperText"),
			value: true,
		}];
		if (!subtypeAllLocked) {
			options.push({
				name: locale.resource(actionResource("OnlyUpdateDSLines")),
				helperTextRes: actionResource("OnlyUpdateDSLinesHelperText"),
				value: false,
			});
		}
		return options;
	}

	/**
	 * On save handler
	 */
	public save = async (): Promise<boolean> => {
		const { onSave, onSavePromise } = this;
		onSave(onSavePromise());
		return true;
	};

	/**
	 * Promise to send the request to Restore From Shape
	 */
	public onSavePromise = async (): Promise<JsonResponse> => {
		const { actionController, filteredAddDSLines, filteredDeleteDSLines, restoreAllDSLines, styleSizeChartRevURL, filteredUpdateDSLines } = this;
		const { m_writer } = actionController;
		try {
			const res = await restoreFromShape(styleSizeChartRevURL, restoreAllDSLines, filteredDeleteDSLines, filteredUpdateDSLines, filteredAddDSLines, m_writer);
			return res;
		} catch (err) {
			throw err;
		}
	};

	/** page action message */
	get actionMessage(): string {
		const { dsLinesSelected, locale } = this;
		return locale.resource(actionResource("DSLinesSelected"), [dsLinesSelected]);
	}

	/* Filter Dimensions */
	getFilteredDimensions(searchText: string, dimensions: CheckboxTreeNode): CheckboxTreeNode {
		if (searchText && dimensions.hasChildren) {
			const newFilteredDimensions = CheckboxTreeNode.createCheckboxTreeNode(dimensions, false);
			const filteredNodes = dimensions.children.filter((v) => {
				return getSlimCardAttrsAsString(v.uniqueId, this.cache)
					.toLocaleLowerCase()
					.includes(searchText);
			});
			filteredNodes.forEach((e) => {
				newFilteredDimensions.addChild(e);
			});
			return newFilteredDimensions;
		}
		return dimensions;
	}

	/* Make search text lower case for use in filtering Dimensions */
	@action onChangeSearchTextField = (event): void => {
		this.searchText = event.target.value;
		const trimmedText = this.searchText.trim()
			.toLocaleLowerCase();
		this.filteredAddDSLines = this.getFilteredDimensions(trimmedText, this.addDSLines);
		this.filteredDeleteDSLines = this.getFilteredDimensions(trimmedText, this.deleteDSLines);
		this.filteredUpdateDSLines = this.getFilteredDimensions(trimmedText, this.updateDSLines);
	};
}

export const RestoreFromShapeStoreContext = createContext<RestoreFromShapeStore>(undefined);
export const useRestoreFromShapeStore = (): RestoreFromShapeStore => useContext(RestoreFromShapeStoreContext);